import React, { useState, useEffect } from "react";
import { Box, Fade } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

interface ScrollIndicatorProps {
  /** Show on mobile only (default: true) */
  mobileOnly?: boolean;
  /** Custom color (default: primary.main) */
  color?: string;
  /** Position from bottom in px (default: 100) */
  bottomOffset?: number;
  /** Auto-hide after user scrolls (default: true) */
  autoHide?: boolean;
}

/**
 * Floating scroll indicator to hint users about scrollable content
 * Features:
 * - Animated bouncing chevron
 * - Auto-hides after first scroll
 * - Mobile-first design
 * - Customizable colors and positioning
 */
export const ScrollIndicator: React.FC<ScrollIndicatorProps> = ({
  mobileOnly = true,
  color = "primary.main",
  bottomOffset = 100,
  autoHide = true,
}) => {
  const [show, setShow] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    // Check if user has already scrolled on ANY screen (global flag)
    const globalScrollKey = "scroll_indicator_dismissed_global";
    const dismissed = localStorage.getItem(globalScrollKey);

    if (dismissed) {
      setShow(false);
      return;
    }

    // Hide after first scroll if autoHide is enabled
    const handleScroll = () => {
      if (window.scrollY > 50 && !hasScrolled) {
        setHasScrolled(true);
        if (autoHide) {
          setShow(false);
          // Set global flag so it won't show on any screen anymore
          localStorage.setItem(globalScrollKey, "true");
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasScrolled, autoHide]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <Fade in={show} timeout={800}>
        <Box
          sx={{
            position: "fixed",
            bottom: bottomOffset,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999,
            display: mobileOnly ? { xs: "flex", md: "none" } : "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            pointerEvents: "none",
          }}
        >
          {/* "Scroll Down" Text */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          >
            <Box
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                px: 2,
                py: 0.5,
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: color,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Scroll Down
              </Box>
            </Box>
          </motion.div>

          {/* Animated Chevrons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: -1,
            }}
          >
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0.3, y: 0 }}
                animate={{
                  opacity: [0.3, 1, 0.3],
                  y: [0, 8, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: index * 0.2,
                  ease: "easeInOut",
                }}
              >
                <KeyboardArrowDown
                  sx={{
                    fontSize: 32,
                    color: color,
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                  }}
                />
              </motion.div>
            ))}
          </Box>
        </Box>
      </Fade>
    </AnimatePresence>
  );
};

/**
 * Bottom fade gradient overlay to indicate more content
 */
export const BottomFadeOverlay: React.FC<{
  show?: boolean;
  height?: number;
  color?: string;
}> = ({ show = true, height = 120, color = "#ffffff" }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      // Hide when user scrolls near bottom
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;
      setVisible(!scrolledToBottom);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!show || !visible) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: height,
        background: `linear-gradient(to top, ${color} 0%, transparent 100%)`,
        pointerEvents: "none",
        zIndex: 998,
        display: { xs: "block", md: "none" }, // Mobile only
      }}
    />
  );
};

export default ScrollIndicator;
